import React, { useEffect, useState, useRef } from 'react';
import './AddressAutocomplete.css';

/**
 * Google Places Autocomplete address input.
 *
 * Requires Google Maps JS API to be loaded with the `places` library.
 * Load script in index.html or via a loader; we load it lazily here from
 * REACT_APP_GOOGLE_MAPS_API_KEY when this component mounts.
 *
 * onChange fires with a normalized address object:
 * {
 *   formatted: "123 Main St, Lindenhurst, NY 11757, USA",
 *   placeId: "ChIJ...",
 *   street: "123 Main St",
 *   city: "Lindenhurst",
 *   state: "NY",
 *   zip: "11757",
 *   lat: 40.6,
 *   lng: -73.4
 * }
 *
 * Or with null if the user typed but didn't select a suggestion.
 */

let scriptLoadingPromise = null;

/**
 * Loads the Google Maps JS API using Google's official bootstrap loader.
 * Returns a promise that resolves when the Places library is fully ready.
 *
 * The bootstrap snippet defines `google.maps.importLibrary()`, which is the
 * supported way to wait for a specific library when loading async. Just
 * waiting for `script.onload` is NOT enough — with loading=async, the
 * 'places' sub-namespace may not yet be defined when onload fires.
 */
function loadGoogleMaps(apiKey) {
  if (typeof window === 'undefined') return Promise.reject(new Error('SSR'));
  if (window.google?.maps?.places?.Autocomplete) return Promise.resolve();
  if (scriptLoadingPromise) return scriptLoadingPromise;

  scriptLoadingPromise = new Promise((resolve, reject) => {
    // Install Google's bootstrap loader (only once)
    if (!window.google?.maps?.importLibrary) {
      // This is the official snippet from Google docs, condensed.
      // It defines window.google.maps.importLibrary as a function that
      // injects the maps script on first call and returns a promise.
      (function (g) {
        var h, a, k, p = "The Google Maps JavaScript API",
            c = "google", l = "importLibrary",
            q = "__ib__", m = document, b = window;
        b = b[c] || (b[c] = {});
        var d = b.maps || (b.maps = {}),
            r = new Set, e = new URLSearchParams,
            u = function () {
              return h || (h = new Promise(function (f, n) {
                a = m.createElement("script");
                e.set("libraries", [...r] + "");
                for (k in g) e.set(k.replace(/[A-Z]/g, function (t) {
                  return "_" + t.toLowerCase();
                }), g[k]);
                e.set("callback", c + ".maps." + q);
                a.src = "https://maps.googleapis.com/maps/api/js?" + e;
                d[q] = f;
                a.onerror = function () { h = n(Error(p + " could not load.")); };
                a.nonce = m.querySelector("script[nonce]")?.nonce || "";
                m.head.append(a);
              }));
            };
        d[l] ? console.warn(p + " only loads once. Ignoring:", g) :
          d[l] = function (f, ...n) { return r.add(f) && u().then(function () { return d[l](f, ...n); }); };
      })({ key: apiKey, v: "weekly" });
    }

    // Now ask for the 'places' library specifically — this resolves only
    // when places is actually ready to use.
    window.google.maps.importLibrary("places")
      .then(() => resolve())
      .catch((err) => reject(err));
  });
  return scriptLoadingPromise;
}

function parsePlace(place) {
  if (!place || !place.address_components) return null;
  const get = (type) => {
    const c = place.address_components.find((c) => c.types.includes(type));
    return c ? c.long_name : '';
  };
  const getShort = (type) => {
    const c = place.address_components.find((c) => c.types.includes(type));
    return c ? c.short_name : '';
  };
  const streetNumber = get('street_number');
  const route = get('route');
  const subpremise = get('subpremise');
  return {
    formatted: place.formatted_address || '',
    placeId: place.place_id || '',
    street: [streetNumber, route].filter(Boolean).join(' '),
    unit: subpremise || null,
    city:
      get('locality') ||
      get('sublocality') ||
      get('sublocality_level_1') ||
      get('neighborhood') ||
      '',
    state: getShort('administrative_area_level_1'),
    zip: get('postal_code'),
    lat: place.geometry?.location?.lat() ?? null,
    lng: place.geometry?.location?.lng() ?? null,
  };
}

export default function AddressAutocomplete({
  value,
  onChange,
  placeholder = 'Start typing your address...',
  required = false,
  id,
  name,
  disabled = false,
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [text, setText] = useState(value?.formatted || '');
  const [ready, setReady] = useState(false);

  // Keep input text in sync if parent resets value
  useEffect(() => {
    setText(value?.formatted || '');
  }, [value?.formatted]);

  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      console.warn('[AddressAutocomplete] REACT_APP_GOOGLE_MAPS_API_KEY not set');
      return;
    }
    let cancelled = false;
    loadGoogleMaps(apiKey)
      .then(() => {
        if (cancelled || !inputRef.current) return;
        const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
          types: ['address'],
          componentRestrictions: { country: 'us' },
          fields: ['address_components', 'formatted_address', 'geometry', 'place_id'],
        });
        ac.addListener('place_changed', () => {
          const place = ac.getPlace();
          const parsed = parsePlace(place);
          if (parsed) {
            setText(parsed.formatted);
            onChange?.(parsed);
          }
        });
        autocompleteRef.current = ac;
        setReady(true);
      })
      .catch((err) => {
        console.error('[AddressAutocomplete] Failed to load Google Maps', err);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <input
      ref={inputRef}
      type="text"
      id={id}
      name={name}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        // Clear the structured value if user types after selecting
        if (value) onChange?.(null);
      }}
      placeholder={ready ? placeholder : 'Loading address lookup...'}
      required={required}
      disabled={disabled || !ready}
      autoComplete="off"
      className="rh-address-autocomplete"
    />
  );
}
